import React from 'react';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="prose prose-lg max-w-none">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">ReferPal Privacy Policy</h1>

            <p className="text-sm text-gray-600 mb-6">
              <strong>Effective Date:</strong> January 1, 2026<br />
              <strong>Last Updated:</strong> November 19, 2025
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-6">
              ReferPal ("ReferPal", "we", "us", "our") is committed to protecting your privacy and personal data.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you
              use our platform, mobile applications, websites, and related services (collectively, the "Services").
            </p>
            <p className="text-gray-700 mb-6">
              This Privacy Policy should be read in conjunction with our Terms of Service. By using the Services,
              you consent to the data practices described in this policy.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.1 Information You Provide Directly</h3>

            <h4 className="text-lg font-medium text-gray-800 mt-4 mb-2">Account Information:</h4>
            <ul className="list-disc list-inside text-gray-700 mb-4">
              <li>Full name, email address, phone number</li>
              <li>Password and authentication credentials</li>
              <li>Profile information (headline, location, languages, work preferences)</li>
              <li>User type designation (job seeker or referrer)</li>
            </ul>

            <h4 className="text-lg font-medium text-gray-800 mt-4 mb-2">Professional Information:</h4>
            <ul className="list-disc list-inside text-gray-700 mb-4">
              <li>Work history and employment details</li>
              <li>Educational background</li>
              <li>Skills and certifications</li>
              <li>Company information (for referrers)</li>
              <li>Position and seniority level</li>
              <li>Verification documents (for referrers)</li>
            </ul>

            <h4 className="text-lg font-medium text-gray-800 mt-4 mb-2">Resume and Application Materials:</h4>
            <ul className="list-disc list-inside text-gray-700 mb-4">
              <li>Resume files and documents</li>
              <li>Cover letters</li>
              <li>Portfolio materials</li>
              <li>Any other documents you upload</li>
            </ul>

            <h4 className="text-lg font-medium text-gray-800 mt-4 mb-2">Communications:</h4>
            <ul className="list-disc list-inside text-gray-700 mb-4">
              <li>Messages exchanged with other users</li>
              <li>Communications with our support team</li>
              <li>Survey responses and feedback</li>
            </ul>

            <h4 className="text-lg font-medium text-gray-800 mt-4 mb-2">Payment Information:</h4>
            <ul className="list-disc list-inside text-gray-700 mb-6">
              <li>Payment method details (processed by third-party payment processors)</li>
              <li>Billing address</li>
              <li>Transaction history</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.2 Information Collected Automatically</h3>

            <h4 className="text-lg font-medium text-gray-800 mt-4 mb-2">Usage Data:</h4>
            <ul className="list-disc list-inside text-gray-700 mb-4">
              <li>IP address, browser type, and version</li>
              <li>Device information (type, operating system, unique device identifiers)</li>
              <li>Pages viewed, time spent on pages, navigation paths</li>
              <li>Referring/exit pages and URLs</li>
              <li>Click data and interaction patterns</li>
            </ul>

            <h4 className="text-lg font-medium text-gray-800 mt-4 mb-2">Device Permissions and Access:</h4>
            <ul className="list-disc list-inside text-gray-700 mb-4">
              <li><strong>Camera Access:</strong> Used for taking profile photos and enabling video calls between users</li>
              <li><strong>Microphone Access:</strong> Required for voice communications and video calls within the platform</li>
              <li><strong>Storage Access:</strong> Used to save and access uploaded documents, resumes, and profile photos</li>
            </ul>

            <h4 className="text-lg font-medium text-gray-800 mt-4 mb-2">Location Data:</h4>
            <ul className="list-disc list-inside text-gray-700 mb-4">
              <li>Geographic location derived from IP address</li>
              <li>Precise location data (if you grant permission)</li>
            </ul>

            <h4 className="text-lg font-medium text-gray-800 mt-4 mb-2">Cookies and Tracking Technologies:</h4>
            <ul className="list-disc list-inside text-gray-700 mb-6">
              <li>Cookies, web beacons, and similar technologies to track activity and store information</li>
              <li>Session identifiers and analytics data</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We use collected information for the following purposes:</p>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.1 Providing and Improving Services</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4">
              <li>Creating and managing your account</li>
              <li>Facilitating connections between job seekers and referrers</li>
              <li>Matching users with relevant opportunities</li>
              <li>Processing transactions and managing subscriptions</li>
              <li>Providing customer support</li>
              <li>Analyzing and improving platform functionality</li>
              <li>Developing new features and services</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.2 Talent Matching and Recruitment Products</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4">
              <li><strong>Creating searchable candidate databases</strong> for employers and recruiters</li>
              <li><strong>Developing talent matching algorithms</strong> and recommendation systems</li>
              <li><strong>Enabling employer discovery</strong> of candidate profiles based on qualifications</li>
              <li><strong>Facilitating direct outreach</strong> from employers to candidates</li>
              <li><strong>Building analytics products</strong> that provide insights to employers and recruiters</li>
              <li><strong>Creating aggregate and anonymized datasets</strong> for recruitment market research</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. Your Rights and Choices</h2>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.1 Access and Portability</h3>
            <p className="text-gray-700 mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-gray-700 mb-4">
              <li>Access the personal data we hold about you</li>
              <li>Request a copy of your data in a structured, commonly used, machine-readable format</li>
              <li>Transmit your data to another service provider (data portability)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.2 Correction and Deletion</h3>
            <p className="text-gray-700 mb-4">You may:</p>
            <ul className="list-disc list-inside text-gray-700 mb-4">
              <li>Update or correct your account information through account settings</li>
              <li>Request deletion of your account and associated personal data</li>
              <li>Note: We may retain certain information as required by law or for legitimate business purposes</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.3 Opt-Out Rights</h3>
            <p className="text-gray-700 mb-4">You may opt out of:</p>
            <ul className="list-disc list-inside text-gray-700 mb-4">
              <li>Marketing communications (via unsubscribe links or account settings)</li>
              <li>Employer discovery features (via privacy settings)</li>
              <li>Non-essential cookies (via browser settings)</li>
              <li>Push notifications (via device or app settings)</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. Data Security</h2>
            <p className="text-gray-700 mb-4">
              We implement appropriate technical and organizational measures to protect your personal data against
              unauthorized access, alteration, disclosure, or destruction, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4">
              <li>Encryption of data in transit (TLS/SSL) and at rest</li>
              <li>Secure authentication mechanisms</li>
              <li>Access controls and authorization systems</li>
              <li>Regular security assessments and audits</li>
              <li>Employee training on data protection</li>
              <li>Incident response procedures</li>
            </ul>
            <p className="text-gray-700 mb-6">
              However, no method of transmission over the Internet or electronic storage is 100% secure.
              While we strive to use commercially acceptable means to protect your personal data, we cannot
              guarantee its absolute security.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. International Data Transfers</h2>
            <p className="text-gray-700 mb-4">
              Your personal data may be transferred to, processed, and stored in countries outside the European
              Economic Area (EEA), including the United States. When transferring personal data outside the EEA,
              we implement appropriate safeguards, including Standard Contractual Clauses (SCCs) approved by the
              European Commission.
            </p>
            <p className="text-gray-700 mb-6 font-semibold">
              BY USING THE SERVICES, YOU ACKNOWLEDGE THAT YOUR PERSONAL DATA WILL BE TRANSFERRED TO AND PROCESSED
              IN COUNTRIES THAT MAY NOT PROVIDE THE SAME LEVEL OF DATA PROTECTION AS YOUR HOME COUNTRY, AND YOU
              CONSENT TO SUCH TRANSFERS.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices,
              please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700">
                <strong>Email:</strong><br />
                • General Privacy Inquiries: <a href="mailto:info@referpal.app" className="text-blue-600 hover:text-blue-800">info@referpal.app</a><br />
                • Data Subject Requests: <a href="mailto:info@referpal.app" className="text-blue-600 hover:text-blue-800">info@referpal.app</a>
              </p>
            </div>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>BY USING THE SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTOOD THIS PRIVACY POLICY.</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;